/**
 * Build the 1-2 letter monogram shown when a user's avatar image cannot be
 * drawn — the S3 object is gone, or the render endpoint did not answer.
 *
 * Prefers the display name, because that is what the person chose to be called.
 * Falls back to the username, which is where the separator handling earns its
 * keep: usernames are `first.last`, `first_last` or `first-last` far more often
 * than they contain a space, and splitting on whitespace alone turned
 * "jon.snyder" into "J" instead of "JS".
 *
 * Returns null when neither yields a letter or digit, so the caller can fall
 * back to the silhouette rather than draw an empty circle.
 *
 * Separate from `getFactiiiInitials` on purpose. That one splits on whitespace
 * only and skips leading punctuation, because factiii names are titles that
 * often open with "#" or an emoji. A username's punctuation is a separator, not
 * decoration, so the two rules genuinely differ — sharing one would break one
 * of them.
 */
export const getUserInitials = (
  name: string | null | undefined,
  username?: string | null
): string | null => {
  const initialsFrom = (value: string | null | undefined): string | null => {
    if (!value) return null;

    const initials = value
      .trim()
      // Whitespace and the separators a username actually uses. A run of them
      // collapses, so "jon..snyder" does not produce an empty middle word.
      .split(/[\s._-]+/)
      .map((word) => word.match(/[\p{L}\p{N}]/u)?.[0] ?? '')
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();

    return initials || null;
  };

  return initialsFrom(name) ?? initialsFrom(username);
};
